import type { TooltipItem, TooltipModel } from 'chart.js';
import { useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { Flex, Space, VFlex } from '@/components/base';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import useInsightExplorerFeedbackListModal from '@/features/InsightExplorer/hooks/useInsightExplorerFeedbackListModal';
import { buildInsightExplorerPeakAnalysisOptions } from '@/features/InsightExplorer/lib/build-insight-explorer-peak-analysis-options';
import {
  InsightExplorerByTypeMap,
  type InsightExplorerFieldType,
  InsightExplorerFieldTypeMap,
  type InsightExplorerTrendViewType,
  type InsightExplorerViewType,
} from '@/features/InsightExplorer/types/constants';
import { insightExplorerUtils } from '@/features/InsightExplorer/utils/insight-explorer.utils';
import useColor from '@/hooks/domain/useColor';
import { animation } from '@/styles/style';
import zIndex from '@/styles/zIndex';
import { getQuadrant } from '@/utils/dom';
import { RangeUnit } from '@/utils/getDatePreset';
import {
  DATE_FORMAT,
  getTimezoneDayjs,
  parseDateFormatByRangeUnit,
} from '@/utils/time';
import { track } from '@/utils/track/track';

import type { HierarchyNodeData } from '../../InsightExplorerSidebar/HierarchyNavigator/HierarchyNavigator';

type TooltipProps = {
  tooltip: TooltipModel<'line'>;
  from: 'total' | 'subtopic';
  meta: {
    field: InsightExplorerFieldType;
    selectedNode: HierarchyNodeData;
    view: InsightExplorerViewType;
    viewType: RangeUnit;
    trendViewType: InsightExplorerTrendViewType;
    filterPayload: StructuredFilter;
    findNode: (alias: string) => HierarchyNodeData | null;
  };
  hideTooltip?: () => void;
};

export const TOTAL_ALIAS = 'total';

const ExplorerChartCustomTooltip = (props: TooltipProps) => {
  const {
    field,
    selectedNode,
    view,
    viewType,
    filterPayload,
    findNode,
    trendViewType,
  } = props.meta;
  const tooltipRef = useRef<HTMLDivElement>(null);
  const color = useColor();
  const openInsightExplorerFeedbackListModal =
    useInsightExplorerFeedbackListModal();

  const label = useMemo(() => {
    const data = props.tooltip.dataPoints[0];
    if (viewType === RangeUnit.Weekly) {
      const { start, end } = parseDateFormatByRangeUnit(data.label, viewType);
      return `${getTimezoneDayjs(start).format(
        DATE_FORMAT.readable,
      )} - ${getTimezoneDayjs(end).format(DATE_FORMAT.readable)}`;
    }
    return data.label;
  }, [props.tooltip.dataPoints, viewType]);

  const openChartRawDetailModal = useCallback(
    (dataPointIndex: number) => {
      if (!selectedNode) return;

      const dataPoint = props.tooltip.dataPoints[
        dataPointIndex
      ] as TooltipItem<'line'> & {
        dataset: {
          alias: string;
        };
      };
      // label is dateString
      const dateString = dataPoint.label;
      const dateRange = parseDateFormatByRangeUnit(dateString, viewType);

      const targetNode = findNode(dataPoint.dataset.alias);
      if (!targetNode) throw new Error('node is required');

      track(AMP_KEYS.INSIGHT_EXPLORER_CHART_TOOLTIP_CLICK, {
        node_type: InsightExplorerByTypeMap.get(targetNode.node_type),
        node_name: targetNode.name,
        from: props.from,
      });

      const sFilter = insightExplorerUtils.getFilterByExplorerOptions({
        trendViewType,
        view: view,
        filterPayload: filterPayload,
        selectedNode: targetNode,
      });

      sFilter.setDateFilter({
        start_date: dateRange.start,
        end_date: dateRange.end,
      });

      const filter = sFilter.get();
      openInsightExplorerFeedbackListModal({
        filter,
        peakAnalysis: buildInsightExplorerPeakAnalysisOptions({
          filter,
          viewType,
          chartName: `${targetNode.name} trend`,
        }),
      });
    },
    [
      selectedNode,
      props.tooltip.dataPoints,
      props.from,
      viewType,
      findNode,
      trendViewType,
      view,
      filterPayload,
      openInsightExplorerFeedbackListModal,
    ],
  );

  const body = useMemo(() => {
    return (
      <LineChartTooltipBody
        tooltip={props.tooltip}
        openChartRawDetailModal={openChartRawDetailModal}
      />
    );
  }, [props.tooltip, openChartRawDetailModal]);

  const pos = useMemo(() => {
    const chartRect = props.tooltip.chart.canvas.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();
    const tooltipWidth = tooltipRect?.width || 220;
    const tooltipHeight = tooltipRect?.height || 150;

    const quadrant = getQuadrant(
      {
        width: chartRect.width,
        height: chartRect.height,
      },
      {
        top: props.tooltip._eventPosition.y,
        left: props.tooltip._eventPosition.x,
      },
    );

    const center = {
      x: props.tooltip._eventPosition.x - tooltipWidth / 2,
      y: props.tooltip._eventPosition.y - tooltipHeight / 2,
      x2: props.tooltip._eventPosition.x + tooltipWidth / 2,
      y2: props.tooltip._eventPosition.y + tooltipHeight / 2,
    };
    switch (quadrant) {
      case 1:
        return {
          right:
            center.x2 > chartRect.width - 10 ? 10 : chartRect.width - center.x2,
          top: center.y < 10 ? 10 : center.y,
        };
      case 2:
        return {
          left: center.x < 10 ? 10 : center.x,
          top: center.y < 10 ? 10 : center.y,
        };
      case 3:
        return {
          left: center.x < 10 ? 10 : center.x,
          bottom:
            center.y2 > chartRect.height - 10
              ? 10
              : chartRect.height - center.y2,
        };
      case 4:
        return {
          right:
            center.x2 > chartRect.width - 10 ? 10 : chartRect.width - center.x2,
          bottom:
            center.y2 > chartRect.height - 10
              ? 10
              : chartRect.height - center.y2,
        };
    }
  }, [props.tooltip]);

  return (
    <TooltipStyle
      ref={tooltipRef}
      onMouseLeave={props.hideTooltip}
      style={{
        ...pos,
        position: 'absolute',
        zIndex: zIndex.chartTooltip,
        border: `1px solid ${color.gray300}`,
        boxShadow: `0px 2px 16px ${color.FD08}`,
        backgroundColor: color.gray100,
        borderRadius: 10,
        pointerEvents: 'auto',
        userSelect: 'auto',
      }}
    >
      <Typo type="label3" color={color.gray700}>
        {label}
      </Typo>
      <Space h={18} />
      <Typo type="label3" color={color.gray600}>
        {InsightExplorerFieldTypeMap.get(field)}
      </Typo>
      <Space h={8} />
      <VFlex g={8}>{body}</VFlex>
    </TooltipStyle>
  );
};

const TooltipStyle = styled(VFlex)`
  padding: 12px;
  min-width: 200px;
  white-space: nowrap;
  animation: ${animation.fadeIn} 0.2s ease-in-out;
  .rect {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    background-color: ${(props) => props.theme.colors.gray300};
  }
`;

const LegendStyle = styled(Flex)`
  flex: 1;
  cursor: pointer;
  padding: 4px 8px;
  white-space: nowrap;
  background-color: ${(props) => props.theme.colors.ND04};
  border-radius: 4px;
  transition: all 0.2s;
  &:hover {
    background-color: ${(props) => props.theme.colors.ND08};
  }
`;

const LineChartTooltipBody = (props: {
  tooltip: TooltipModel<'line'>;
  openChartRawDetailModal: (dataPointIndex: number) => void;
}) => {
  const color = useColor();
  const { tooltip, openChartRawDetailModal } = props;
  return tooltip.body.map((item, i) => {
    const nodeData = tooltip.dataPoints[i];
    const colorString = nodeData.dataset.backgroundColor as string;
    const bgColor =
      typeof colorString === 'string' ? colorString : color.primary100;

    return (
      <LegendStyle
        g={8}
        a="center"
        key={i}
        onClick={() => openChartRawDetailModal(i)}
      >
        <div
          className="rect"
          style={{
            backgroundColor: bgColor,
          }}
        ></div>
        <Typo type="label3" color={color.gray600} weight="sb">
          {item.lines.map((node) => node)}
        </Typo>
      </LegendStyle>
    );
  });
};

export default ExplorerChartCustomTooltip;
